import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import { TableMetricsBreakdownCell } from '@/features/shared/components/tableSystem/TableMetricsBreakdownCell';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { buildCoreColumns } from '@/features/shared/components/tableSystem/testsCoreColumns';
import { mapChipKeyToStatusTone } from '@/features/shared/utils/tableMetricPresentation';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import { getStatusCopy } from '@/shared/copy/status';
import { formatDuration, formatFrequency, formatPercent } from '@/ui';
import {
  getDateFilterOperators,
  getNumericFilterOperators,
  getSingleSelectFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { getThresholdChipKey } from '@/ui/composites/table';
import { mapTestRunSummariesWithRunNumbers } from '@/ui/composites/table/tableUtils';
import { type GridColDef } from '@/ui/primitives';
import { buildHiddenFilterColumns } from './testsColumns.hiddenFilterColumns';
import type { TestsColumnsOptions } from './testsColumns.types';
import type { MetricThresholdGroup, TestListItemDto } from '@flowqa/shared';

const columnWidths = tableColumnWidths.tests;

export const buildTestsColumns = ({
  selectedProjectId,
  groupErroredWithFailed = false,
  preferFileNameOverDisplayName = false,
  resolveGroupErroredWithFailed,
  resolvePreferFileNameOverDisplayName,
  onProjectSelect,
  onStatusSelect,
  thresholds,
  resolveThresholds,
  showSuiteLinks,
  combineNameAndSuite = false,
  includeSuiteInNameColumn = true,
  includeTagsInNameColumn = false,
  includeActionsColumn = false,
  disableColumnResizeHandles = false,
  latestAsMeta = false,
  includeLastExecutedColumn = true,
  showDeltas = true,
  disableLinks = false,
  metricSeriesByTestId: _metricSeriesByTestId,
  filterSuites = [],
  filterTags = [],
  onEditRow,
  onDeleteRow,
  deleteRowPendingId = null,
  tagColorByName = {},
}: TestsColumnsOptions): Array<GridColDef<TestListItemDto>> => {
  const singleSelectOperators = getSingleSelectFilterOperators();
  const dateOperators = getDateFilterOperators();
  const numericOperators = getNumericFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: TestListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };

  const { nameColumn, suiteColumn, latestSummaryColumn, lastExecutedColumn } = buildCoreColumns({
    groupErroredWithFailed,
    preferFileNameOverDisplayName,
    resolveGroupErroredWithFailed,
    resolvePreferFileNameOverDisplayName,
    onProjectSelect,
    onStatusSelect,
    showSuiteLinks,
    combineNameAndSuite,
    includeSuiteInNameColumn,
    includeTagsInNameColumn,
    latestAsMeta,
    includeLastExecutedColumn,
    disableLinks,
    tagColorByName,
    columnFlex: {
      name: columnWidths.name.flex,
      suite: columnWidths.suite.flex,
      latest: columnWidths.latestStatus.flex,
      lastExecuted: columnWidths.lastExecuted.flex,
    },
  });

  const metricsColumn: GridColDef<TestListItemDto> = {
    field: 'metricsSummary',
    headerName: getDetailLabel('metrics'),
    flex: columnWidths.metrics.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const passRate = params.row.metrics?.passRate ?? null;
      const passRateDelta = deltasEnabled ? (params.row.metrics?.delta?.passRate ?? null) : null;
      const frequency = params.row.metrics?.frequencyPerDay ?? null;
      const frequencyDelta = deltasEnabled
        ? (params.row.metrics?.delta?.frequencyPerDay ?? null)
        : null;
      const flakeScore = params.row.metrics?.flakeScore ?? null;
      const flakeScoreDelta = deltasEnabled
        ? (params.row.metrics?.delta?.flakeScore ?? null)
        : null;
      const avgDuration = params.row.metrics?.averageDurationMs ?? null;
      const avgDurationDelta = deltasEnabled
        ? (params.row.metrics?.delta?.averageDurationMs ?? null)
        : null;
      const resolvedPassRate = passRate;
      const resolvedPassRateDelta = resolvedPassRate === null ? null : passRateDelta;
      const resolvedFrequency = frequency;
      const resolvedFrequencyDelta = resolvedFrequency === null ? null : frequencyDelta;
      const resolvedFlakeScore = flakeScore;
      const resolvedFlakeScoreDelta = resolvedFlakeScore === null ? null : flakeScoreDelta;
      const resolvedAvgDuration = avgDuration;
      const resolvedAvgDurationDelta = resolvedAvgDuration === null ? null : avgDurationDelta;
      const resolvedAvgDurationThresholdValue =
        resolvedAvgDuration === null ? null : resolvedAvgDuration / 1000;
      const thresholdGroup = resolvedThresholds(params.row);
      const passRateTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedPassRate, thresholdGroup.passRate),
      );
      const frequencyTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedFrequency, thresholdGroup.frequency),
      );
      const flakeScoreTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedFlakeScore, thresholdGroup.flakeScore),
      );
      const avgDurationTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedAvgDurationThresholdValue, thresholdGroup.avgDuration),
      );

      const passRateLabel =
        resolvedPassRate === null ? getStatusCopy('noData') : `${formatPercent(resolvedPassRate)}%`;
      const passRateDeltaLabel =
        resolvedPassRateDelta === null
          ? null
          : resolvedPassRateDelta === 0
            ? getStatusCopy('noChange')
            : `${resolvedPassRateDelta > 0 ? '+' : ''}${formatPercent(resolvedPassRateDelta)}%`;
      const frequencyLabel =
        resolvedFrequency === null ? formatFrequency(0) : formatFrequency(resolvedFrequency);
      const frequencyDeltaLabel =
        resolvedFrequencyDelta === null
          ? null
          : resolvedFrequencyDelta === 0
            ? getStatusCopy('noChange')
            : `${resolvedFrequencyDelta > 0 ? '+' : resolvedFrequencyDelta < 0 ? '-' : ''}${formatFrequency(
                Math.abs(resolvedFrequencyDelta),
              )}`;
      const flakeScoreLabel =
        resolvedFlakeScore === null
          ? getStatusCopy('noData')
          : `${formatPercent(resolvedFlakeScore)}%`;
      const flakeScoreDeltaLabel =
        resolvedFlakeScoreDelta === null
          ? null
          : resolvedFlakeScoreDelta === 0
            ? getStatusCopy('noChange')
            : `${resolvedFlakeScoreDelta > 0 ? '+' : ''}${formatPercent(resolvedFlakeScoreDelta)}%`;
      const avgDurationLabel =
        resolvedAvgDuration === null
          ? getStatusCopy('noData')
          : formatDuration(resolvedAvgDuration);
      const avgDurationDeltaLabel =
        resolvedAvgDurationDelta === null
          ? null
          : resolvedAvgDurationDelta === 0
            ? getStatusCopy('noChange')
            : `${resolvedAvgDurationDelta > 0 ? '+' : '-'}${formatDuration(
                Math.abs(resolvedAvgDurationDelta),
                { roundMillisecondsWithSeconds: true },
              )}`;

      return (
        <TableCellContainer gap="none">
          <TableMetricsBreakdownCell
            items={[
              {
                key: 'passRate',
                label: getChartTitle('passRate'),
                valueLabel: passRateLabel,
                deltaValue: resolvedPassRateDelta,
                deltaLabel: passRateDeltaLabel,
                isMissing: resolvedPassRate === null,
                tone: passRateTone,
                direction: thresholdGroup.passRate.direction,
              },
              {
                key: 'frequency',
                label: getChartTitle('frequency'),
                valueLabel: frequencyLabel,
                deltaValue: resolvedFrequencyDelta,
                deltaLabel: frequencyDeltaLabel,
                isMissing: resolvedFrequency === null,
                tone: frequencyTone,
                direction: thresholdGroup.frequency.direction,
              },
              {
                key: 'avgDuration',
                label: getChartTitle('avgDuration'),
                valueLabel: avgDurationLabel,
                deltaValue: resolvedAvgDurationDelta,
                deltaLabel: avgDurationDeltaLabel,
                isMissing: resolvedAvgDuration === null,
                tone: avgDurationTone,
                direction: thresholdGroup.avgDuration.direction,
              },
              {
                key: 'flakeScore',
                label: getChartTitle('flakeScore'),
                valueLabel: flakeScoreLabel,
                deltaValue: resolvedFlakeScoreDelta,
                deltaLabel: flakeScoreDeltaLabel,
                isMissing: resolvedFlakeScore === null,
                tone: flakeScoreTone,
                direction: thresholdGroup.flakeScore.direction,
              },
            ]}
          />
        </TableCellContainer>
      );
    },
  };

  const executionsColumn: GridColDef<TestListItemDto> = {
    field: 'executionStatusCounts',
    headerName: 'Runs',
    flex: columnWidths.executions.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-breakdown-cell',
    renderCell: (params) => {
      const counts = params.row.executionStatusCounts;

      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="execution"
            totalIconEntity="test"
            counts={{
              passed: counts?.passed ?? 0,
              failed: counts?.failed ?? 0,
              errored: counts?.errored ?? 0,
              skipped: counts?.skipped ?? 0,
              untested: counts?.untested ?? 0,
            }}
            summaryItems={mapTestRunSummariesWithRunNumbers(
              params.row.executionSummaries,
              'display',
            )}
            summaryUnitLabel="test"
            totalLinkMode="execution"
          />
        </TableCellContainer>
      );
    },
  };

  const { suiteFilterColumn, durationFilterColumn, timestampFilterColumn, statusFilterColumn } =
    buildHiddenFilterColumns({
      singleSelectOperators,
      numericOperators,
      dateOperators,
      filterSuites,
      filterTags,
    });

  const columns: Array<GridColDef<TestListItemDto>> = [
    nameColumn,
    ...(combineNameAndSuite ? [suiteFilterColumn] : showSuiteLinks ? [suiteColumn] : []),
    latestSummaryColumn,
    ...(latestAsMeta ? [executionsColumn] : []),
    ...(latestAsMeta ? [metricsColumn] : []),
    ...(lastExecutedColumn ? [lastExecutedColumn] : []),
    ...(latestAsMeta ? [statusFilterColumn, durationFilterColumn, timestampFilterColumn] : []),
    ...(includeActionsColumn
      ? [
          {
            field: 'actions',
            headerName: '',
            flex: columnWidths.actions.flex,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
              <TableActionsCell
                rowName={params.row.name}
                editActionLabel={getEntityActionLabel('edit', 'Test')}
                deleteActionLabel={getEntityActionLabel('delete', 'Test')}
                onEdit={() => {
                  onEditRow?.(params.row);
                }}
                onDelete={() => {
                  onDeleteRow?.(params.row);
                }}
                disableEdit={deleteRowPendingId === params.row.id}
                disableDelete={deleteRowPendingId === params.row.id}
              />
            ),
          } satisfies GridColDef<TestListItemDto>,
        ]
      : []),
  ];

  if (!disableColumnResizeHandles) {
    return columns;
  }

  return columns.map((column) => ({
    ...column,
    resizable: false,
  }));
};
